import React, { FormEvent, useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { Box, Button, Typography } from "@mui/material";

interface CheckoutFormProps {
  paymentMode: "single" | "subscription";
  onSuccess: () => void; // chiamato quando conferma lato client -> qui usato per animazione checkmark
  handleClose: () => void;
  setProcessing?: (v: boolean) => void; // parent setter to show overlay
  clientSecret: string; // Aggiunto per CardElement
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ paymentMode, onSuccess, handleClose, setProcessing, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setMessage(null);
    setProcessing?.(true);

    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!
        }
      });

      if (result.error) {
        setMessage(result.error.message || "Errore durante il pagamento.");
        setIsProcessing(false);
        setProcessing?.(false);
      } else if (result.paymentIntent?.status === "succeeded") {
        onSuccess();
        setIsProcessing(false);
        setProcessing?.(false);
      } else {
        const status = result.paymentIntent?.status;
        setMessage(
          status === "requires_action"
            ? "È necessaria un'ulteriore verifica per completare il pagamento."
            : status === "processing"
              ? "Pagamento in elaborazione. Attendi la conferma."
              : `Pagamento non completato${status ? ` (stato: ${status})` : ""}.`
        );
        setIsProcessing(false);
        setProcessing?.(false);
      }
    } catch (err: any) {
      setMessage(err?.message || "Errore inaspettato");
      setIsProcessing(false);
      setProcessing?.(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 2 }}>
        <CardElement options={{
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        }} />
      </Box>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={isProcessing || !stripe || !elements}
        sx={{ py: 1.25, mb: 1.5 }}
      >
        {isProcessing ? "⏳ Elaborazione…" : (paymentMode === "single" ? "Paga ora" : "Attiva abbonamento")}
      </Button>

      <Button variant="text" fullWidth onClick={handleClose} disabled={isProcessing} sx={{ mb: 1 }}>
        Annulla
      </Button>

      {message && <Typography color="error" mt={1}>{message}</Typography>}
    </form>
  );
};

export default CheckoutForm;
